import { useMemo } from 'react';
import { describeNodeType, type JourneyGraph, type JourneyNode } from '@mailflow/core/segments';

/**
 * Zeichenbereich für einen Ablauf.
 *
 * Der Aufbau wird BERECHNET, nicht gespeichert: eine Ebene je Abstand vom
 * Auslöser, Geschwister nebeneinander. Damit kann kein Ablauf kaputt
 * aussehen, weil jemand einen Knoten irgendwohin geschoben hat, und ein aus
 * einer Vorlage erzeugter Graph sieht sofort richtig aus — ohne dass ihn
 * jemand erst zurechtrücken muss.
 *
 * Gespeicherte Koordinaten wären der übliche Weg und hier der falsche: sie
 * müssten mit jeder Graphänderung nachgeführt werden, und ein Knoten ohne
 * Koordinaten (aus einer älteren Fassung, aus der API) läge auf 0,0.
 *
 * Bewusst kein React Flow: 40 kB Abhängigkeit für Kästen mit Linien, deren
 * Bedienung auf dem Telefon ohnehin nicht trägt.
 */

const NODE_WIDTH = 200;
const NODE_HEIGHT = 76;
const GAP_X = 40;
const GAP_Y = 56;

interface Placed {
  node: JourneyNode;
  x: number;
  y: number;
  level: number;
}

interface Layout {
  placed: Placed[];
  width: number;
  height: number;
}

function layoutGraph(graph: JourneyGraph): Layout {
  const entry = graph.nodes.find((node) => node.type === 'trigger') ?? graph.nodes[0];
  if (!entry) return { placed: [], width: 0, height: 0 };

  // Breitensuche ab dem Auslöser: der Abstand vom Start ist die Ebene.
  const level = new Map<string, number>([[entry.id, 0]]);
  const queue = [entry.id];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLevel = level.get(current)!;
    for (const edge of graph.edges.filter((e) => e.from === current)) {
      // Der größere Abstand gewinnt: ein Knoten, den zwei Zweige erreichen,
      // gehört unter den späteren — sonst kreuzen sich die Linien.
      const existing = level.get(edge.to);
      if (existing === undefined || existing < currentLevel + 1) {
        level.set(edge.to, currentLevel + 1);
        queue.push(edge.to);
      }
    }
  }

  // Unerreichbare Knoten hängen unten dran, statt zu verschwinden — sie sind
  // ein Befund, kein Grund, sie zu verstecken.
  const maxLevel = Math.max(0, ...level.values());
  for (const node of graph.nodes) {
    if (!level.has(node.id)) level.set(node.id, maxLevel + 1);
  }

  const byLevel = new Map<number, JourneyNode[]>();
  for (const node of graph.nodes) {
    const l = level.get(node.id)!;
    byLevel.set(l, [...(byLevel.get(l) ?? []), node]);
  }

  const breiteste = Math.max(...[...byLevel.values()].map((nodes) => nodes.length), 1);
  const width = breiteste * NODE_WIDTH + (breiteste - 1) * GAP_X;

  const placed: Placed[] = [];
  for (const [l, nodes] of byLevel) {
    const zeilenbreite = nodes.length * NODE_WIDTH + (nodes.length - 1) * GAP_X;
    const startX = (width - zeilenbreite) / 2;
    nodes.forEach((node, index) => {
      placed.push({
        node,
        x: startX + index * (NODE_WIDTH + GAP_X),
        y: l * (NODE_HEIGHT + GAP_Y),
        level: l,
      });
    });
  }

  const height = (Math.max(...byLevel.keys()) + 1) * (NODE_HEIGHT + GAP_Y);
  return { placed, width, height };
}

const BRANCH_LABELS: Record<string, string> = {
  yes: 'ja',
  no: 'nein',
  timeout: 'Zeit abgelaufen',
  next: '',
};

export function JourneyCanvas({
  graph,
  stats,
  onSelect,
  selectedId,
}: {
  graph: JourneyGraph;
  stats?: Record<string, { entered: number; yes_count: number; no_count: number }>;
  onSelect?: (nodeId: string) => void;
  selectedId?: string | null;
}) {
  const layout = useMemo(() => layoutGraph(graph), [graph]);
  const byId = useMemo(() => new Map(layout.placed.map((p) => [p.node.id, p])), [layout]);

  if (layout.placed.length === 0) {
    return <p className="muted text-sm">Der Ablauf hat noch keine Knoten.</p>;
  }

  const padding = 24;

  return (
    <div className="canvas">
      <svg
        className="canvas__svg"
        viewBox={`${-padding} ${-padding} ${layout.width + padding * 2} ${layout.height + padding * 2}`}
        style={{ minWidth: layout.width + padding * 2 }}
        role="img"
        aria-label={`Ablaufdiagramm mit ${graph.nodes.length} Schritten`}
      >
        <defs>
          <marker
            id="pfeil"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" className="canvas__arrowhead" />
          </marker>
        </defs>

        {graph.edges.map((edge) => {
          const from = byId.get(edge.from);
          const to = byId.get(edge.to);
          if (!from || !to) return null;

          const x1 = from.x + NODE_WIDTH / 2;
          const y1 = from.y + NODE_HEIGHT;
          const x2 = to.x + NODE_WIDTH / 2;
          const y2 = to.y;
          // Bézier statt gerader Linie: bei versetzten Kästen bleibt die
          // Zuordnung sonst schwer zu verfolgen.
          const mitte = (y1 + y2) / 2;

          return (
            <g key={`${edge.from}-${edge.branch}-${edge.to}`}>
              <path
                d={`M ${x1} ${y1} C ${x1} ${mitte}, ${x2} ${mitte}, ${x2} ${y2}`}
                className="canvas__edge"
                data-branch={edge.branch}
                markerEnd="url(#pfeil)"
              />
              {BRANCH_LABELS[edge.branch] && (
                <text
                  x={(x1 + x2) / 2}
                  y={mitte - 4}
                  className="canvas__edge-label"
                  data-branch={edge.branch}
                  textAnchor="middle"
                >
                  {BRANCH_LABELS[edge.branch]}
                </text>
              )}
            </g>
          );
        })}

        {layout.placed.map(({ node, x, y }) => {
          const stat = stats?.[node.id];
          return (
            <g
              key={node.id}
              transform={`translate(${x}, ${y})`}
              className="canvas__node"
              data-type={node.type}
              data-selected={selectedId === node.id ? 'true' : undefined}
              onClick={() => onSelect?.(node.id)}
              role={onSelect ? 'button' : undefined}
              tabIndex={onSelect ? 0 : undefined}
              onKeyDown={(event) => {
                if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onSelect(node.id);
                }
              }}
            >
              <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={8} className="canvas__box" />
              <text x={12} y={26} className="canvas__title">
                {truncate(node.label ?? describeNodeType(node), 24)}
              </text>
              <text x={12} y={46} className="canvas__subtitle">
                {describeNodeType(node)}
              </text>
              {stat && (
                <text x={12} y={64} className="canvas__stat">
                  {stat.entered.toLocaleString('de-CH')} durchlaufen
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
